"use client";
import React from "react";
import { PageManifestoSchema } from "@/sanity/pages/PageManifestoTemplate";
import NavBarV2 from "@/components/global/partials/HeaderV2";
import Footer from "@/components/global/partials/Footer";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PortableText } from "next-sanity";
import Image from "@/helpers/Image";
import chart from "@/public/manifesto/chart.svg";
import statue from "@/public/manifesto/differenceImage.png";
import differenceBg from "@/public/manifesto/differenceBg.jpg";
import missionBg from "@/public/manifesto/missionBg.png";
import missionBgM from "@/public/manifesto/missionBgMobile.png";
import missionLogo from "@/public/manifesto/missionLogo.svg";
import missionImage from "@/public/manifesto/missionImage.png";
import CryptoPillar from "@/components/global/ui/CryptoPillar";
import { motion } from "framer-motion";

export default function DifferenceSection({
  data,
}: {
  data: PageManifestoSchema;
}) {
  return (
    <>
      <section id="difference">
        <div className="w-[min(94.25rem,100%)] mx-auto flex flex-col gap-[4rem]">
          <div className="relative overflow-clip bg-[#08240C] rounded-[3rem] px-[2.75rem] lg:px-[5.88rem] lg:pt-[7rem] py-[4.75rem] lg:min-h-[39rem]">
            <Image
              src={differenceBg}
              alt=""
              className="absolute inset-0 z-0 object-cover object-right !h-full !w-[60rem] lg:!w-full"
            />
            <Image
              src={statue}
              alt=""
              className="absolute bottom-0 right-[-2.25rem] lg:right-0 z-0 !w-[32.375rem] lg:!w-[32.375rem]"
            />
            <CryptoPillar
              className="left-[4.88rem] lg:left-[50%] h-[20rem] lg:!scale-100"
              trailColors="bg-[#FFFFFF]/[0.35]"
              type="ethereum"
            />
            <CryptoPillar
              className="bottom-0 left-[11.1rem] lg:left-[calc(50%+6.5rem)] h-[30rem] lg:h-[25rem] lg:!scale-[1.08]"
              trailColors="bg-[#FFFFFF]/[0.72]"
              type="bitcoin"
            />
            <CryptoPillar
              className="bottom-0 left-[17.4rem] lg:left-[calc(50%+13rem)] h-[15rem] lg:h-[10rem] lg:!scale-[1]"
              trailColors="bg-[#FFFFFF]/[0.39]"
              type="solana"
            />

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0,
                ease: [0.25, 0.4, 0.25, 1],
              }}
              viewport={{ once: true }}
              className="lg:w-[37.125rem] min-h-[52rem] lg:min-h-[26.5rem] relative z-[1] flex flex-col gap-[1.5rem]"
            >
              {data?.differenceTitle && (
                <div
                  className="richtext richtext-start w-full
                  prose-h2:text-white prose-h2:text-[4rem] lg:prose-h2:text-[4.5rem] prose-h2:mb-[3rem] prose-h2:leading-[101%] prose-h2:font-medium"
                >
                  <PortableText
                    value={data.differenceTitle}
                    components={richTextComponent}
                  />
                </div>
              )}
              {data?.differenceSubtitle && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.8,
                    delay: 0,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                  viewport={{ once: true }}
                  className="richtext richtext-start
                  prose-p:text-[2rem] prose-p:text-white/80"
                >
                  <PortableText
                    value={data.differenceSubtitle}
                    components={richTextComponent}
                  />
                </motion.div>
              )}
            </motion.div>
          </div>

          <div className="px-[2rem] lg:px-[7rem]">
            {data?.differenceContent && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
                viewport={{ once: true }}
                className="richtext richtext-start gap-[2rem]
                prose-p:my-0 prose-h4:my-0
                prose-h3:text-[#103037]/80 prose-h3:font-medium prose-h3:leading-[130%] prose-h3:text-[3.25rem]"
              >
                <PortableText
                  value={data.differenceContent}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0,
                ease: [0.25, 0.4, 0.25, 1],
              }}
              viewport={{ once: true }}
              className="mt-[4rem] relative overflow-clip bg-[#B8D8F0] rounded-[3rem] px-[2.75rem] lg:px-[3rem] py-[4.75rem] lg:min-h-[39rem]"
            >
              <Image
                src={missionBg}
                alt=""
                className="hidden lg:block absolute top-0 right-0 z-0 !h-full !w-[60rem] lg:!w-[58.125rem]"
              />
              <Image
                src={missionBgM}
                alt=""
                className="lg:hidden absolute top-0 right-0 z-0 !h-full !w-full"
              />
              <Image
                src={missionImage}
                alt=""
                className="absolute bottom-0 right-1/2 translate-x-1/2 lg:translate-x-0 lg:right-0 z-0 !w-[40rem] lg:!w-[39.625rem]"
              />

              <div className="lg:w-[38.75rem] min-h-[57rem] lg:min-h-[29.5rem] relative z-[1] flex flex-col lg:justify-between gap-[2rem]">
                <Image src={missionLogo} alt="" className="!w-[3.875rem]" />
                {data?.differenceMission && (
                  <div
                    className="richtext richtext-start w-full
                    prose-h3:text-[3rem] prose-h3:text-[#103037]/80 prose-h3:font-medium prose-h3:leading-[130%]"
                  >
                    <PortableText
                      value={data.differenceMission}
                      components={richTextComponent}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
